//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Box, Paper, Tab } from '@mui/material'
import { PoolTabEnum, PoolTypeEnum, RoleEnum } from '@shared/Enums'
import { EthAddr } from '@shared/SchemaTypes'
import React, { ReactElement, useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'
import PoolContractAddresses from '@react/components/pools/PoolContractAddresses'
import PoolLegalPage from '@react/components/pools/PoolLegalPage'
import PoolSummary from '@react/components/pools/PoolSummary'

import { ETH_ADDR_ZERO } from '@shared/SchemaTypes'
import { PoolBaseOverviewCTO, PoolDynamicOverviewCTO, PoolFlexOverviewCTO, PoolOverviewCTO } from '@shared/sharedTypes'

import LoanList from '@react/components/loans/LoanList'
import PoolOverview from '@react/lender/PoolOverview'
import { useOtChainState, useOtSelector } from '@react/redux/store'
import BorrowerAdminPoolActionModals from './BorrowerAdminPoolActionModals'
import { BorrowerWithdrawalTab } from './BorrowerWithdrawalsTab'

import { AdminLenderView } from '@react/admin/AdminLenderView'
import { PoolOffchainDataView } from '@react/admin/PoolOffchainDataView'
import { logOT } from '@shared/loggingShared'
import { BorrowerWithdrawalFlexTab } from './BorrowerWithdrawalsFlexTab'

import { BorrowerDynamicWithdrawalsTab } from '@react/dynamic/BorrowerDynamicWithdrawalsTab'
import DynamicBorrowerAdminPoolActionModals from '@react/dynamic/DynamicBorrowerAdminPoolActionModals'
import { DynamicDepositsTab } from '@react/dynamic/DynamicDepositsTab'
import { displayName } from '@shared/PoolBaseCTO'
import CollateralView from '@react/admin/CollateralView'
import TicketPendingCard from '@react/admin/TicketPendingCard'

export default function BorrowerPoolDetails(props: { poolAddr?: EthAddr }): ReactElement {
  const navigate = useNavigate()
  const p = useParams().poolAddr
  const poolAddr = (props.poolAddr ? props.poolAddr : p) as EthAddr

  const [searchParams, setSearchParams] = useSearchParams()

  const {
    userConnection: { roleEnum },
  } = useOtChainState()
  const [selectedTab, setSelectedTab] = React.useState<PoolTabEnum>(
    (searchParams.get('tab') as PoolTabEnum) ?? PoolTabEnum.OVERVIEW
  )
  const poolBaseOverviewCTO = useOtSelector(
    (state) => state.poolState.poolOverviewCTOs[poolAddr ?? ETH_ADDR_ZERO] as PoolBaseOverviewCTO | undefined
  )

  const allPools = useOtSelector((state) => state.poolState.poolOverviewCTOs)
  const poolOverviewCTO =
    poolBaseOverviewCTO?.poolType === PoolTypeEnum.Term ? (poolBaseOverviewCTO as PoolOverviewCTO) : null
  const poolFlexOverviewCTO =
    poolBaseOverviewCTO?.poolType === PoolTypeEnum.FlexibleRate ? (poolBaseOverviewCTO as PoolFlexOverviewCTO) : null
  const poolDynamicOverviewCTO =
    poolBaseOverviewCTO?.poolType === PoolTypeEnum.Dynamic ? (poolBaseOverviewCTO as PoolDynamicOverviewCTO) : null

  const handleChange = (_event: React.SyntheticEvent, newValue: PoolTabEnum): void => {
    setSelectedTab(newValue ?? PoolTabEnum.OVERVIEW)
    setSearchParams({ tab: newValue })
  }
  useEffect(() => {
    if (poolAddr && !poolBaseOverviewCTO && Object.keys(allPools).length > 0) {
      logOT.info('location navigate to /')
      navigate('/')
    }
  }, [poolAddr, poolBaseOverviewCTO, allPools])

  useEffect(() => {
    const tabParam = searchParams.get('tab')

    setSelectedTab((tabParam as PoolTabEnum) ?? PoolTabEnum.OVERVIEW)
  }, [searchParams])

  logOT.info('BorrowerPoolDetails poolAddr:', poolAddr)
  if (poolAddr && poolBaseOverviewCTO) {
    return (
      <Box>
        <TicketPendingCard poolAddr={poolAddr} />
        {poolDynamicOverviewCTO && (
          <DynamicBorrowerAdminPoolActionModals poolAddr={poolAddr} poolName={displayName(poolBaseOverviewCTO)} />
        )}
        {!poolDynamicOverviewCTO && (
          <BorrowerAdminPoolActionModals poolName={displayName(poolBaseOverviewCTO)} poolAddr={poolAddr} />
        )}
        <PoolSummary poolAddr={poolAddr} />
        <Paper sx={{ marginTop: 4, marginBottom: 4 }}>
          <Box sx={{ width: '100%' }}>
            <TabContext value={selectedTab}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <TabList
                  onChange={handleChange}
                  aria-label="lab API tabs example"
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  <Tab label={'Overview'} value={PoolTabEnum.OVERVIEW} />
                  {/* <Tab label={'Schedule'} */}
                  {/*     value={PoolTabEnum.SCHEDULE}/> */}
                  {poolDynamicOverviewCTO && <Tab label={'Deposits'} value={PoolTabEnum.DEPOSITS} />}
                  <Tab label={'Withdrawals'} value={PoolTabEnum.WITHDRAWS} />
                  {poolOverviewCTO && <Tab label={'Loans'} value={PoolTabEnum.LOANS} />}
                  {(roleEnum === RoleEnum.POOL_ADMIN || roleEnum === RoleEnum.BORROWER) && (
                    <Tab label={'Lender View'} value={PoolTabEnum.LENDER_VIEW} />
                  )}
                  <Tab label={'OffChain Data'} value={PoolTabEnum.OFFCHAIN_DATA} />
                  <Tab label={'Info'} value={PoolTabEnum.LEGAL} />

                  <Tab label={'Collateral'} value={PoolTabEnum.ASSETS} />
                  <Tab label={'Contract Addresses'} value={PoolTabEnum.CONTRACT_ADDRESSES} />
                </TabList>
              </Box>
              <TabPanel value={PoolTabEnum.OVERVIEW}>
                <PoolOverview />
              </TabPanel>
              {/* <TabPanel value={PoolTabEnum.SCHEDULE}><PoolSchedule/></TabPanel> */}
              {poolOverviewCTO && (
                <TabPanel value={PoolTabEnum.WITHDRAWS}>
                  <BorrowerWithdrawalTab poolAddr={poolAddr} />
                </TabPanel>
              )}
              {poolFlexOverviewCTO && (
                <TabPanel value={PoolTabEnum.WITHDRAWS}>
                  <BorrowerWithdrawalFlexTab poolAddr={poolAddr} />
                </TabPanel>
              )}
              {poolDynamicOverviewCTO && (
                <TabPanel value={PoolTabEnum.DEPOSITS}>
                  <DynamicDepositsTab poolAddr={poolAddr} />
                </TabPanel>
              )}
              {poolDynamicOverviewCTO && (
                <TabPanel value={PoolTabEnum.WITHDRAWS}>
                  <BorrowerDynamicWithdrawalsTab poolAddr={poolAddr} />
                </TabPanel>
              )}
              {(roleEnum === RoleEnum.POOL_ADMIN || roleEnum === RoleEnum.BORROWER) && (
                <TabPanel value={PoolTabEnum.LENDER_VIEW}>
                  <AdminLenderView poolAddr={poolAddr} />
                </TabPanel>
              )}
              {poolOverviewCTO && (
                <TabPanel value={PoolTabEnum.LOANS}>
                  <LoanList />
                </TabPanel>
              )}
              <TabPanel value={PoolTabEnum.LEGAL}>
                <PoolLegalPage />
              </TabPanel>
              <TabPanel value={PoolTabEnum.OFFCHAIN_DATA}>
                <PoolOffchainDataView poolAddr={poolAddr} />
              </TabPanel>
              <TabPanel value={PoolTabEnum.ASSETS}>
                <CollateralView poolAddr={poolAddr} />
              </TabPanel>
              <TabPanel value={PoolTabEnum.CONTRACT_ADDRESSES}>
                <PoolContractAddresses role={RoleEnum.BORROWER} />
              </TabPanel>
            </TabContext>
          </Box>
        </Paper>
      </Box>
    )
  } else {
    return <></>
  }
}
